/**
 * Shared URL builder for network table row links so "open in new tab" works.
 * Each page passes its context; getRowTo returns the target path for (row, tabType).
 */

export type NetworkRowToContext =
  | { page: "network"; networkId: string }
  | { page: "zone"; networkId: string; zoneId: string }
  | { page: "room"; networkId: string; roomId: string; zoneId?: string }
  | { page: "hub"; networkId: string; hubId: string }
  | { page: "group"; networkId: string; groupId: string };

function controllerDetailsPath(basePath: string, row: any): string {
  if (row.UID != null && row.said != null) {
    return `${basePath}/controller-details/${row.UID}-${row.said}`;
  }
  if (row.name != null) {
    return `${basePath}/controller-details/${row.name}`;
  }
  return "#";
}

function getRowToNetwork(id: string, row: any, tabType?: string): string {
  switch (tabType) {
    case "zones":
      return `/network-details/${id}/zone/${encodeURIComponent(row.name)}${
        row.uuid ? `?uuid=${encodeURIComponent(row.uuid)}` : ""
      }`;
    case "rooms":
      return `/network-details/${id}/room-details/${encodeURIComponent(row.name)}${
        row.uuid ? `?uuid=${encodeURIComponent(row.uuid)}` : ""
      }`;
    case "sensors":
      return `/network-details/${id}/sensor-details/${row.ldsu_uuid}-${row.SAID}`;
    case "controllers":
      return controllerDetailsPath(`/network-details/${id}`, row);
    case "hubs":
      return `/network-details/${id}/hub-details/${encodeURIComponent(row.name)}${
        row.uuid ? `?uuid=${encodeURIComponent(row.uuid)}` : ""
      }`;
    case "auxiliaryHubs": {
      const auxId = row.uuid ?? row.UUID ?? row.uid ?? row.UID ?? row.name;
      return `/network-details/${id}/auxiliary-hub-details/${auxId}`;
    }
    case "groups":
      return `/network-details/${id}/group-details/${encodeURIComponent(row.name)}${
        row.uuid ? `?uuid=${encodeURIComponent(row.uuid)}` : ""
      }`;
    default:
      return "#";
  }
}

function getRowToZone(id: string, zoneId: string, row: any, tabType?: string): string {
  const basePath = `/network-details/${id}/zone/${zoneId}`;
  switch (tabType) {
    case "rooms":
      return `${basePath}/room-details/${encodeURIComponent(row.name)}${
        row.uuid ? `?uuid=${encodeURIComponent(row.uuid)}` : ""
      }`;
    case "sensors":
      return `${basePath}/sensor-details/${row.ldsu_uuid}-${row.SAID}`;
    case "controllers":
      return controllerDetailsPath(basePath, row);
    default:
      return "#";
  }
}

function getRowToRoom(
  id: string,
  roomId: string,
  zoneId: string | undefined,
  row: any,
  tabType?: string
): string {
  const roomPath = zoneId
    ? `/network-details/${id}/zone/${zoneId}/room-details/${roomId}`
    : `/network-details/${id}/room-details/${roomId}`;
  switch (tabType) {
    case "sensors":
      return `${roomPath}/sensor-details/${row.ldsu_uuid}-${row.SAID}`;
    case "controllers":
      return controllerDetailsPath(roomPath, row);
    default:
      return "#";
  }
}

function getRowToHub(id: string, hubId: string, row: any, tabType?: string): string {
  const hubPath = `/network-details/${id}/hub-details/${hubId}`;
  switch (tabType) {
    case "sensors":
      return `${hubPath}/sensor-details/${row.ldsu_uuid}-${row.SAID}`;
    case "controllers":
      return controllerDetailsPath(hubPath, row);
    default:
      return "#";
  }
}

function getRowToGroup(id: string, groupId: string, row: any, tabType?: string): string {
  const groupPath = `/network-details/${id}/group-details/${groupId}`;
  switch (tabType) {
    case "sensors":
      return `${groupPath}/sensor-details/${row.ldsu_uuid}-${row.SAID}`;
    case "controllers":
      return controllerDetailsPath(groupPath, row);
    default:
      return "#";
  }
}

/**
 * Returns the target path for a table row link, or "#" if not applicable.
 * Used so middle-click / "Open link in new tab" navigates to the correct detail page.
 */
export function getRowToForContext(
  context: NetworkRowToContext,
  row: any,
  tabType?: string
): string {
  const id = context.networkId;
  if (!id || id === "undefined") return "#";

  switch (context.page) {
    case "network":
      return getRowToNetwork(id, row, tabType);
    case "zone":
      return context.zoneId ? getRowToZone(id, context.zoneId, row, tabType) : "#";
    case "room":
      return context.roomId ? getRowToRoom(id, context.roomId, context.zoneId, row, tabType) : "#";
    case "hub":
      return context.hubId ? getRowToHub(id, context.hubId, row, tabType) : "#";
    case "group":
      return context.groupId ? getRowToGroup(id, context.groupId, row, tabType) : "#";
    default:
      return "#";
  }
}
